import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Button,
  SimpleGrid,
  Badge,
  HStack,
  Link,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FaSearch, FaChevronRight } from "react-icons/fa";
import PageSeo from "../../../seo/PageSeo";
import { SITE_URL, DEFAULT_KEYWORDS } from "../../../seo/siteConfig";
import { breadcrumbSchema } from "../../../seo/structuredData";
import BLOG_ARTICLES, { BLOG_CATEGORIES } from "../../../content/blog/articles";

const BlogIndex = () => {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const muted = useColorModeValue("gray.600", "gray.400");
  const deep = useColorModeValue("gray.900", "white");
  const cardBg = useColorModeValue("whiteAlpha.900", "gray.800");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return BLOG_ARTICLES.filter((a) => {
      if (category !== "All" && a.category !== category) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        a.metaDescription.toLowerCase().includes(q) ||
        a.keywords.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
      );
    });
  }, [category, query]);

  const jsonLd = breadcrumbSchema([
    { name: "Home", url: SITE_URL },
    { name: "Blog", url: `${SITE_URL}/blog` },
  ]);

  return (
    <Box as="main" bg="transparent" py={{ base: 10, md: 16 }} minH="70vh">
      <PageSeo
        title="Blog & guides — Screen AI, Media AI, productivity, money, chores"
        description="Long-form guides on Screen AI lecture capture, Media AI study uploads, Portfolio AI expenses, Project AI, Todos AI, Daily Chore reminders, and group bills—problems Manage360 solves."
        keywords={`${DEFAULT_KEYWORDS}, productivity blog, study guides, expense tracking guides`}
        canonicalPath="/blog"
        jsonLd={jsonLd}
      />
      <Container maxW="container.lg" px={{ base: 4, md: 6 }}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading as="h1" size="2xl" color={deep} mb={3}>
              Manage360 blog & guides
            </Heading>
            <Text color={muted} fontSize="lg" lineHeight="tall" maxW="3xl">
              Practical articles on personal AI assistants, screen and media
              capture for learning and work, voice-friendly money and task
              tools, chores, and shared bills—each guide maps to a feature you
              can try free on Manage360.
            </Text>
          </Box>

          <HStack flexWrap="wrap" gap={2}>
            {BLOG_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                size="sm"
                variant={category === cat ? "solid" : "outline"}
                colorScheme="teal"
                onClick={() => setCategory(cat)}
              >
                {cat}
              </Button>
            ))}
          </HStack>

          <InputGroup maxW="md">
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search guides…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              bg={cardBg}
            />
          </InputGroup>

          <Text fontSize="sm" color={muted}>
            {filtered.length} article{filtered.length === 1 ? "" : "s"}
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            {filtered.map((post) => (
              <Box
                key={post.slug}
                as={RouterLink}
                to={`/blog/${post.slug}`}
                bg={cardBg}
                borderRadius="xl"
                borderWidth="1px"
                p={5}
                _hover={{ borderColor: "teal.400", boxShadow: "md" }}
                transition="all 0.2s"
              >
                <Badge colorScheme="teal" mb={2}>
                  {post.category}
                </Badge>
                <Heading as="h2" size="md" color={deep} mb={2}>
                  {post.title}
                </Heading>
                <Text fontSize="sm" color={muted} noOfLines={3} mb={3}>
                  {post.metaDescription}
                </Text>
                <HStack fontSize="xs" color={muted} justify="space-between">
                  <Text>
                    {post.readMinutes} min · {post.publishedAt}
                  </Text>
                  <HStack spacing={1} color="teal.500">
                    <Text>Read</Text>
                    <FaChevronRight />
                  </HStack>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>

          <Box textAlign="center" pt={4}>
            <Button as={RouterLink} to="/register" colorScheme="teal" size="lg">
              Start free on Manage360
            </Button>
            <Text fontSize="sm" color={muted} mt={3}>
              <Link as={RouterLink} to="/faqs" color="teal.500">
                FAQs
              </Link>
              {" · "}
              <Link as={RouterLink} to="/pricing" color="teal.500">
                AI Usage Credits pricing
              </Link>
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default BlogIndex;
